import React from 'react';
import { Card, Box, Typography, useTheme } from '@mui/material';

// Every caller across the app passes one of these exact light-pastel
// {iconBg, iconColor} pairs (confirmed by grepping every KpiCard usage) —
// rather than migrate ~45 call sites to a new semantic-prop API, KpiCard
// itself recognizes the known light pairs and swaps in a dark-mode-safe
// equivalent automatically. Callers never need to know or care which mode
// is active.
const DARK_PAIR_MAP = {
  '#ebf4ff': { bg: '#17304D', color: '#63B3ED' }, // blue / info
  '#ecfdf5': { bg: '#0B3B2E', color: '#34D399' }, // green / success
  '#fffbeb': { bg: '#4A2E12', color: '#F6AD55' }, // orange / warning
  '#fff5f5': { bg: '#4A1C1C', color: '#FC8181' }, // red / danger
  '#fef2f2': { bg: '#4A1C1C', color: '#FC8181' }, // red / danger (alt shade)
  '#f5f3ff': { bg: '#2E1F4D', color: '#B794F4' }, // purple
  '#e9d8fd': { bg: '#2A1B4A', color: '#C4B5FD' }, // purple (alt shade)
};

const KpiCard = ({ label, value, delta, deltaType = 'neutral', icon, iconBg = '#EBF4FF', iconColor = '#2B6CB0' }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const darkPair = isDark ? DARK_PAIR_MAP[String(iconBg).toLowerCase()] : null;
  // Falls back to a translucent tint of whatever iconColor was passed, for
  // any pair not in the known set above, instead of silently rendering the
  // light-mode pastel bg unchanged in dark mode.
  const resolvedBg = darkPair ? darkPair.bg : isDark ? `${iconColor}26` : iconBg;
  const resolvedColor = darkPair ? darkPair.color : iconColor;

  const deltaColors = {
    up: theme.palette.success.main,
    down: theme.palette.error.main,
    neutral: theme.palette.text.disabled,
  };

  return (
    <Card sx={{ p: 2.5, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <Box>
        <Typography sx={{
          fontSize: 11, fontWeight: 600, color: 'text.disabled',
          textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.75,
        }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 26, fontWeight: 700, lineHeight: 1, mb: 0.75, color: 'text.primary' }}>
          {value}
        </Typography>
        {delta && (
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: deltaColors[deltaType] }}>
            {delta}
          </Typography>
        )}
      </Box>
      {icon && (
        <Box sx={{
          width: 42, height: 42, borderRadius: 2,
          bgcolor: resolvedBg, color: resolvedColor,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
          '& svg': { fontSize: 22 },
        }}>
          {icon}
        </Box>
      )}
    </Card>
  );
};

export default KpiCard;
